/* OCR Batch Page - Tam sayfa OCR isleme.
   - Yeni sekmede acilir (Faturalar > Fatura Ekle butonu yonlendirir)
   - Tek tek (1 paralel) upload + paralel polling
   - 10 dk per-file upload timeout
   - 3 deneme otomatik retry (5s/15s/45s backoff)
   - OCR tamamlanan dosyalar otomatik kaydedilir (eksik alan varsa Duzenle bekler)
   - Sayfa kapatma uyarisi (beforeunload)
   Hedef: Sabaha kadar arka planda calissin, hicbir manuel mudahaleye ihtiyac duymasin.
*/
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, X, RefreshCw, CheckCircle2, ArrowLeft, AlertTriangle, Edit2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MAX_OCR_FILES = 500;          // sabaha kadar 500 dosyaya kadar
const UPLOAD_TIMEOUT_MS = 600000;   // 10 dk per file
const POLL_TIMEOUT_MS = 900000;     // 15 dk per file polling
const POLL_INTERVAL = 3000;
const MAX_UPLOAD_RETRIES = 3;
const RETRY_BACKOFFS_MS = [5000, 15000, 45000];   // 5s, 15s, 45s

const OCR_ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const OCR_ALLOWED_EXT_RE = /\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i;

export default function OcrBatchPage() {
    const navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const [hintType, setHintType] = useState('purchase');
    const [dragOver, setDragOver] = useState(false);
    const [autoSave, setAutoSave] = useState(true);
    const [editingId, setEditingId] = useState(null);

    const filesRef = useRef([]);
    useEffect(() => { filesRef.current = files; }, [files]);

    const updateFile = useCallback((id, patch) => {
        setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
    }, []);

    const removeFile = (id) => setFiles(prev => {
        const tgt = prev.find(f => f.id === id);
        if (tgt?.previewUrl) URL.revokeObjectURL(tgt.previewUrl);
        return prev.filter(f => f.id !== id);
    });

    const addFiles = (fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        const accepted = [];
        const rejected = [];
        for (const f of incoming) {
            if (f.size > 20 * 1024 * 1024) { rejected.push(`${f.name}: 20MB ustu`); continue; }
            if (!OCR_ALLOWED_MIMES.includes(f.type) && !OCR_ALLOWED_EXT_RE.test(f.name)) {
                rejected.push(`${f.name}: format desteksiz`); continue;
            }
            accepted.push(f);
        }
        setFiles(prev => {
            const room = MAX_OCR_FILES - prev.length;
            const slice = accepted.slice(0, Math.max(0, room));
            if (accepted.length > room) {
                rejected.push(`${accepted.length - room} dosya eklenmedi (max ${MAX_OCR_FILES})`);
            }
            const mapped = slice.map((f, idx) => ({
                id: `f-${Date.now()}-${prev.length + idx}-${Math.random().toString(36).slice(2, 7)}`,
                file: f,
                previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
                status: 'pending',
                extracted: null,
                error: null,
                _attempt: 0,
            }));
            return [...prev, ...mapped];
        });
        if (rejected.length) toast.error(rejected.join(', '), { duration: 5000 });
    };

    // Sayfa kapatma uyarisi (devam eden is varken)
    useEffect(() => {
        const handler = (e) => {
            const active = filesRef.current.some(f =>
                f.status === 'processing' || f.status === 'uploading' || f.status === 'pending' || f.status === 'saving'
            );
            if (active) {
                e.preventDefault();
                e.returnValue = 'Devam eden tarama var. Cikmak istediginize emin misiniz?';
                return e.returnValue;
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    // ============ UPLOAD + POLL + AUTO-SAVE ============
    // Tek paralel slot - "ag yogun" hatasini engellemek icin
    const uploadLock = useRef(Promise.resolve());

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    const uploadOnce = async (f) => {
        const fd = new FormData();
        fd.append('file', f.file);
        fd.append('hint_type', hintType);
        const resp = await axios.post(
            `${API}/api/accounting/invoices/ocr-extract-async`, fd,
            {
                headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
                timeout: UPLOAD_TIMEOUT_MS,
            }
        );
        return resp.data.job_id;
    };

    const pollUntilDone = async (jobId, fileId) => {
        const start = Date.now();
        while (Date.now() - start < POLL_TIMEOUT_MS) {
            // eslint-disable-next-line no-await-in-loop
            await sleep(POLL_INTERVAL);
            try {
                // eslint-disable-next-line no-await-in-loop
                const sr = await axios.get(
                    `${API}/api/accounting/invoices/ocr-extract-job/${jobId}`,
                    { headers: headers(), timeout: 20000 }
                );
                const s = sr.data;
                if (s.status === 'done') return { ok: true, extracted: s.extracted, warnings: s.warnings || [] };
                if (s.status === 'error') return { ok: false, error: s.error || 'OCR hatasi' };
                updateFile(fileId, {
                    status: 'processing',
                    _elapsed: s.elapsed_sec || 0,
                    _queued: s.status === 'queued',
                    _slow: (s.elapsed_sec || 0) > 60,
                });
            } catch (e) {
                // Polling hatasi gecici olabilir - bir kac kez denemeye devam et
                continue;
            }
        }
        return { ok: false, error: 'Tarama 15dk asti' };
    };

    const isRetryableError = (e) => {
        if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '')) return true;
        if (e.code === 'ERR_NETWORK' || /network/i.test(e.message || '')) return true;
        const st = e.response?.status;
        if (st === 429 || st === 502 || st === 503 || st === 504) return true;
        return false;
    };

    const saveExtracted = async (f, ext) => {
        const payload = {
            type: ext.type || hintType,
            invoice_number: ext.invoice_number || '',
            invoice_date: ext.invoice_date || new Date().toISOString().slice(0, 10),
            due_date: ext.due_date || '',
            contact_name: ext.contact_name || '',
            vat_number: ext.vat_number || '',
            iban: ext.iban || '',
            currency: ext.currency || 'EUR',
            subtotal: ext.subtotal || 0,
            vat_amount: ext.vat_amount || 0,
            total: ext.total || 0,
            payment_reference: ext.payment_reference || '',
            notes: `OCR (${ext._ocr?.filename || f.file.name})`,
            force_duplicate: true,
            skip_recon: true,
        };
        if (!payload.invoice_number || payload.total <= 0) {
            return { ok: false, reason: 'Fatura no veya tutar bos - duzenle' };
        }
        // Save retry: 3 deneme (Cloudflare WAF 403, 429, 502, 503, 504, network)
        const SAVE_RETRIES = 3;
        const SAVE_BACKOFFS = [2000, 5000, 15000];
        let lastErr = null;
        for (let attempt = 1; attempt <= SAVE_RETRIES; attempt++) {
            try {
                await axios.post(`${API}/api/accounting/invoices`, payload, {
                    headers: headers(), timeout: 30000,
                });
                return { ok: true };
            } catch (e) {
                const st = e.response?.status;
                const detail = e.response?.data?.detail;
                // Duplicate kontrolu - 409: retry yapma, hata yok say
                if (st === 409) {
                    return { ok: false, reason: 'Zaten kayitli (duplicate)' };
                }
                // Validation hatalari - retry'a deger degil
                if (st === 400 || st === 422) {
                    const msg = typeof detail === 'string' ? detail : (detail?.message || 'Validation hatasi');
                    return { ok: false, reason: msg };
                }
                // Retryable: 403 (Cloudflare WAF), 429, 5xx, network
                const retryable = !st || st === 403 || st === 429 || (st >= 500 && st <= 599) ||
                    e.code === 'ECONNABORTED' || e.code === 'ERR_NETWORK';
                lastErr = e;
                if (!retryable || attempt >= SAVE_RETRIES) {
                    const msg = typeof detail === 'string' ? detail
                        : (detail?.message || e.message || `Kayit hatasi (${st || 'ag'})`);
                    return { ok: false, reason: msg };
                }
                // eslint-disable-next-line no-await-in-loop
                await sleep(SAVE_BACKOFFS[attempt - 1]);
            }
        }
        const detail = lastErr?.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || lastErr?.message || 'Kayit hatasi');
        return { ok: false, reason: msg };
    };

    const runOcrOne = async (f) => {
        updateFile(f.id, { status: 'pending', _upload_phase: 'queued', error: null });

        // 1) UPLOAD (sira ile, tek tek) + 3 deneme
        let jobId = null;
        let uploadAttempt = 0;
        const myTurn = uploadLock.current.then(async () => {
            while (uploadAttempt < MAX_UPLOAD_RETRIES) {
                uploadAttempt += 1;
                try {
                    updateFile(f.id, {
                        status: 'uploading',
                        _upload_phase: 'uploading',
                        _attempt: uploadAttempt,
                    });
                    return await uploadOnce(f);
                } catch (e) {
                    if (uploadAttempt >= MAX_UPLOAD_RETRIES || !isRetryableError(e)) {
                        throw e;
                    }
                    const backoff = RETRY_BACKOFFS_MS[uploadAttempt - 1] || 45000;
                    updateFile(f.id, {
                        status: 'uploading',
                        _upload_phase: 'retry_wait',
                        _attempt: uploadAttempt,
                        _retry_in: Math.ceil(backoff / 1000),
                    });
                    // eslint-disable-next-line no-await-in-loop
                    await sleep(backoff);
                }
            }
            throw new Error('Yukleme 3 denemede de basarisiz');
        });
        uploadLock.current = myTurn.catch(() => null);

        try {
            jobId = await myTurn;
        } catch (e) {
            const status = e.response?.status;
            let msg;
            if (status === 429) msg = 'Sunucu yogun - 1 dakika sonra otomatik tekrar dener';
            else if (e.code === 'ERR_NETWORK') msg = 'Ag baglantisi kesildi - bir kac saniye sonra otomatik tekrar dener';
            else if (status === 502 || status === 503 || status === 504) msg = `Sunucu yogun (${status}) - otomatik tekrar denenecek`;
            else msg = (e.response?.data?.detail?.message || e.response?.data?.detail || e.message || 'Yukleme hatasi');
            updateFile(f.id, { status: 'error', error: typeof msg === 'string' ? msg : 'Yukleme hatasi', _upload_phase: null });
            return;
        }

        // 2) POLLING - kendi job'i icin
        updateFile(f.id, { status: 'processing', _upload_phase: 'extracting' });
        const result = await pollUntilDone(jobId, f.id);
        if (!result.ok) {
            updateFile(f.id, { status: 'error', error: result.error, _upload_phase: null });
            return;
        }

        const ext = result.extracted;
        const isDuplicate = (result.warnings || []).some(w => w?.type === 'duplicate_invoice');

        if (isDuplicate) {
            // OCR tamamlandi ama bu fatura zaten DB'de var - save'i hic deneme
            const dupMsg = result.warnings.find(w => w?.type === 'duplicate_invoice')?.message || 'Zaten kayitli';
            updateFile(f.id, {
                status: 'saved',         // 'saved' goster (yesil rozet) ki kullanici "atlandi" anlasin
                extracted: ext,
                warnings: result.warnings,
                saved_info: 'Duplicate - atlandi',
                _is_duplicate: true,
                _upload_phase: null,
            });
            // 2sn sonra listeden dus (manuel bulk save'deki gibi)
            setTimeout(() => {
                setFiles(prev => prev.filter(x => x.id !== f.id));
            }, 2000);
            return;
        }

        updateFile(f.id, {
            status: 'done',
            extracted: ext,
            warnings: result.warnings,
            _upload_phase: null,
        });

        // 3) AUTO-SAVE (acik ise + eksik alan yoksa)
        if (autoSave && ext?.invoice_number && (ext?.total || 0) > 0) {
            updateFile(f.id, { status: 'saving' });
            const sv = await saveExtracted(f, ext);
            if (sv.ok) {
                updateFile(f.id, { status: 'saved' });
                // 3sn sonra listeden dus
                setTimeout(() => {
                    setFiles(prev => prev.filter(x => x.id !== f.id));
                }, 3000);
            } else {
                updateFile(f.id, { status: 'done', error: `Kaydedilemedi: ${sv.reason}` });
            }
        } else if (autoSave && (!ext?.invoice_number || (ext?.total || 0) <= 0)) {
            updateFile(f.id, { status: 'done', error: 'Otomatik kayit yapilamadi - alanlari Duzenle' });
        }
    };

    // ============ OTOMATIK ISLEM YONETIMI ============
    // pending dosyalari sirayla baslat. Sirada 1 dosya cikar - upload+poll+save zinciri.
    const inFlightRef = useRef(new Set());
    useEffect(() => {
        const pendings = files.filter(f => f.status === 'pending' && !inFlightRef.current.has(f.id));
        // En fazla 1 dosya UPLOAD asamasinda olur (uploadLock zaten siralasin)
        // ama birden fazla dosya POLLING asamasinda olabilir
        for (const f of pendings) {
            inFlightRef.current.add(f.id);
            runOcrOne(f).finally(() => inFlightRef.current.delete(f.id));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files.map(f => `${f.id}:${f.status}`).join(',')]);

    // ============ MANUEL "TEKRAR" - hatali olanlari pending'e cevir ============
    const retryAllErrors = () => {
        setFiles(prev => prev.map(f =>
            f.status === 'error' ? { ...f, status: 'pending', error: null, _attempt: 0 } : f
        ));
    };

    // ============ "TUM HATALI KAYITLARI YENIDEN KAYDET" - done state'te kalip kaydedilemeyenleri retry et ============
    const [bulkRetrying, setBulkRetrying] = useState(false);
    const retryFailedSaves = async () => {
        const failed = filesRef.current.filter(f =>
            f.status === 'done' && f.error && f.extracted?.invoice_number && (f.extracted?.total || 0) > 0
        );
        if (!failed.length) { toast('Yeniden kaydedilecek dosya yok'); return; }
        setBulkRetrying(true);
        let success = 0;
        let stillFailed = 0;
        for (const f of failed) {
            updateFile(f.id, { status: 'saving', error: null });
            // eslint-disable-next-line no-await-in-loop
            const sv = await saveExtracted(f, f.extracted);
            if (sv.ok) {
                success += 1;
                updateFile(f.id, { status: 'saved' });
                setTimeout(() => setFiles(prev => prev.filter(x => x.id !== f.id)), 2000);
            } else {
                stillFailed += 1;
                updateFile(f.id, { status: 'done', error: `Kayit: ${sv.reason}` });
            }
            // Cloudflare WAF / rate-limit'i tetiklememek icin save'ler arasi kucuk bekleme
            // eslint-disable-next-line no-await-in-loop
            await sleep(400);
        }
        setBulkRetrying(false);
        toast.success(`${success} kaydedildi · ${stillFailed} hala basarisiz`);
    };

    const saveAllDone = async () => {
        const ready = filesRef.current.filter(f =>
            f.status === 'done' && f.extracted?.invoice_number && (f.extracted?.total || 0) > 0
        );
        if (!ready.length) { toast.error('Kaydedilecek hazir dosya yok'); return; }
        for (const f of ready) {
            updateFile(f.id, { status: 'saving' });
            // eslint-disable-next-line no-await-in-loop
            const sv = await saveExtracted(f, f.extracted);
            if (sv.ok) {
                updateFile(f.id, { status: 'saved' });
                setTimeout(() => setFiles(prev => prev.filter(x => x.id !== f.id)), 2000);
            } else {
                updateFile(f.id, { status: 'done', error: `Kayit: ${sv.reason}` });
            }
        }
        // sonunda recon
        try {
            await axios.post(`${API}/api/accounting/reconciliation/auto`, {}, { headers: headers() });
        } catch (_e) { /* ignore */ }
        toast.success('Toplu kayit tamamlandi');
    };

    // ============ HESAPLAMALAR ============
    const counts = {
        pending: files.filter(f => f.status === 'pending').length,
        uploading: files.filter(f => f.status === 'uploading').length,
        processing: files.filter(f => f.status === 'processing').length,
        done: files.filter(f => f.status === 'done').length,
        saving: files.filter(f => f.status === 'saving').length,
        saved: files.filter(f => f.status === 'saved').length,
        duplicate: files.filter(f => f._is_duplicate).length,
        error: files.filter(f => f.status === 'error').length,
    };
    // Save'i basarisiz olmus (done + error mesaji var) dosyalar
    const failedSaveCount = files.filter(f =>
        f.status === 'done' && f.error && f.extracted?.invoice_number && (f.extracted?.total || 0) > 0
    ).length;
    const total = files.length;
    const processed = counts.done + counts.saved + counts.error;
    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
    const active = counts.pending + counts.uploading + counts.processing + counts.saving;

    // ============ UI ============
    const editingFile = files.find(f => f.id === editingId);

    return (
        <div className="min-h-screen bg-[#FAFAFA] p-4 md:p-8" data-testid="ocr-batch-page">
            <div className="max-w-5xl mx-auto">
                {/* HEADER */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/accounting')}
                            className="h-9 w-9 flex items-center justify-center bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6]"
                            title="Geri don"
                            data-testid="ocr-batch-back-btn"
                        >
                            <ArrowLeft size={16} />
                        </button>
                        <div>
                            <h1 className="text-lg md:text-xl font-bold text-[#111] flex items-center gap-2">
                                <Sparkles size={18} className="text-purple-600" />
                                Toplu OCR Tarama
                            </h1>
                            <p className="text-[11px] text-[#6B7280]">
                                Bu sekme acik kaldigi surece arka planda calisir · Diger sayfalarda rahatca gezinin
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" data-testid="ocr-batch-autosave-toggle">
                            <input
                                type="checkbox"
                                checked={autoSave}
                                onChange={e => setAutoSave(e.target.checked)}
                                className="rounded"
                            />
                            <span className="font-semibold">Otomatik kaydet</span>
                        </label>
                        <select
                            value={hintType}
                            onChange={e => setHintType(e.target.value)}
                            className="h-9 px-2 bg-white border border-[#E5E7EB] rounded-lg text-xs"
                            data-testid="ocr-batch-hint-type"
                        >
                            <option value="purchase">Alis Faturasi</option>
                            <option value="sales">Satis Faturasi</option>
                            <option value="purchase_credit">Alis Iade</option>
                            <option value="sales_credit">Satis Iade</option>
                            <option value="receipt">Fis</option>
                        </select>
                    </div>
                </div>

                {/* PROGRESS BAR */}
                {total > 0 && (
                    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 mb-4 shadow-sm sticky top-4 z-10" data-testid="ocr-batch-progress">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3 text-xs font-semibold text-[#111] flex-wrap">
                                <span>{processed}/{total} islendi</span>
                                {counts.uploading > 0 && <span className="text-amber-600 flex items-center gap-1"><RefreshCw size={11} className="animate-spin" />{counts.uploading} yukleniyor</span>}
                                {counts.processing > 0 && <span className="text-purple-600 flex items-center gap-1"><RefreshCw size={11} className="animate-spin" />{counts.processing} AI okuyor</span>}
                                {counts.saving > 0 && <span className="text-blue-600">{counts.saving} kaydediliyor</span>}
                                {counts.saved > 0 && <span className="text-emerald-700">{counts.saved} kaydedildi</span>}
                                {counts.duplicate > 0 && <span className="text-orange-600">{counts.duplicate} duplicate atlandi</span>}
                                {counts.error > 0 && <span className="text-red-600">{counts.error} hata</span>}
                                {active === 0 && counts.error > 0 && (
                                    <button onClick={retryAllErrors}
                                        className="ml-2 h-7 px-3 bg-amber-100 text-amber-700 text-[11px] font-bold rounded-md hover:bg-amber-200 flex items-center gap-1"
                                        data-testid="ocr-batch-retry-errors">
                                        <RefreshCw size={11} />Hatalilari Tekrar Tara
                                    </button>
                                )}
                                {failedSaveCount > 0 && (
                                    <button onClick={retryFailedSaves} disabled={bulkRetrying}
                                        className="ml-2 h-7 px-3 bg-red-100 text-red-700 text-[11px] font-bold rounded-md hover:bg-red-200 flex items-center gap-1 disabled:opacity-50"
                                        data-testid="ocr-batch-retry-saves">
                                        {bulkRetrying ? <><RefreshCw size={11} className="animate-spin" />Kaydediliyor...</> : <><RefreshCw size={11} />Kaydedilemenleri Tekrar Dene ({failedSaveCount})</>}
                                    </button>
                                )}
                                {!autoSave && counts.done > 0 && (
                                    <button onClick={saveAllDone}
                                        className="ml-2 h-7 px-3 bg-[#111827] text-white text-[11px] font-bold rounded-md flex items-center gap-1"
                                        data-testid="ocr-batch-save-all">
                                        <CheckCircle2 size={11} />Hazirlari Kaydet ({counts.done})
                                    </button>
                                )}
                            </div>
                            <span className="text-sm font-bold text-[#111]">%{pct}</span>
                        </div>
                        <div className="w-full h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${counts.error > 0 ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-amber-400' : 'bg-gradient-to-r from-purple-500 via-pink-500 to-emerald-400'}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        {active > 0 && (
                            <p className="text-[10px] text-[#9CA3AF] mt-2">
                                Tek tek isleniyor (kararli mod). Bu sekmeyi acik birakin, diger sayfalarda gezinebilirsiniz. Sabaha kadar surse bile cikmaz.
                            </p>
                        )}
                    </div>
                )}

                {/* DROP ZONE */}
                <label
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                    className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition mb-4 ${dragOver ? 'border-purple-500 bg-purple-50' : 'border-[#E5E7EB] bg-white hover:border-purple-300 hover:bg-purple-50/30'} ${files.length >= MAX_OCR_FILES ? 'opacity-40 pointer-events-none' : ''}`}
                    data-testid="ocr-batch-drop-zone"
                >
                    <input
                        type="file"
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,image/*"
                        onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                        className="hidden"
                        data-testid="ocr-batch-file-input"
                    />
                    <div className="flex items-center justify-center gap-2">
                        <Sparkles size={16} className="text-purple-600" />
                        <p className="text-sm font-semibold text-[#111]">
                            {files.length === 0 ? `Buraya surukle veya tikla` : `+ Yeni ekle`}
                        </p>
                        <span className="text-xs text-[#9CA3AF]">({files.length}/{MAX_OCR_FILES})</span>
                    </div>
                    <p className="text-[11px] text-[#9CA3AF] mt-1">PDF / PNG / JPG / WEBP · Max 20MB / dosya · Tek tek isleme · 3 deneme otomatik retry</p>
                </label>

                {/* FILE LIST */}
                <div className="space-y-2">
                    {files.length === 0 && (
                        <div className="text-center py-12 text-sm text-[#9CA3AF]" data-testid="ocr-batch-empty">
                            Birden fazla fatura/fis ekleyin · Sabaha kadar bile surse arka planda calisir
                        </div>
                    )}
                    {files.map(f => <FileRow key={f.id} f={f} onEdit={setEditingId} onRemove={removeFile} onRetry={() => updateFile(f.id, { status: 'pending', error: null, _attempt: 0 })} />)}
                </div>
            </div>

            {editingFile && (
                <EditModal
                    file={editingFile}
                    onClose={() => setEditingId(null)}
                    onSave={(updated) => {
                        updateFile(editingFile.id, { extracted: updated, status: 'done', error: null });
                        setEditingId(null);
                        if (autoSave && updated.invoice_number && (updated.total || 0) > 0) {
                            // re-trigger save with updated data
                            setTimeout(async () => {
                                updateFile(editingFile.id, { status: 'saving' });
                                const sv = await saveExtracted(editingFile, updated);
                                if (sv.ok) {
                                    updateFile(editingFile.id, { status: 'saved' });
                                    setTimeout(() => setFiles(prev => prev.filter(x => x.id !== editingFile.id)), 2000);
                                } else {
                                    updateFile(editingFile.id, { status: 'done', error: `Kayit: ${sv.reason}` });
                                }
                            }, 100);
                        }
                    }}
                />
            )}
        </div>
    );
}

// ============ FILE ROW ============
function FileRow({ f, onEdit, onRemove, onRetry }) {
    const ext = f.extracted;
    const fileWarn = ext && (!ext.invoice_number || (ext.total || 0) <= 0);
    const isDup = f._is_duplicate;
    const phaseLabel = () => {
        if (f.status === 'pending') return { txt: 'Sirada bekliyor', cls: 'text-gray-500' };
        if (f.status === 'uploading') {
            if (f._upload_phase === 'retry_wait') return { txt: `${f._attempt}. deneme · ${f._retry_in}s sonra tekrar`, cls: 'text-amber-700' };
            return { txt: f._attempt > 1 ? `Yukleniyor (deneme ${f._attempt}/3)` : 'Yukleniyor...', cls: 'text-amber-600' };
        }
        if (f.status === 'processing') {
            if (f._queued) return { txt: 'AI sirasinda...', cls: 'text-blue-600' };
            if (f._slow) return { txt: `AI okuyor (${f._elapsed}s · yogun)`, cls: 'text-amber-700' };
            return { txt: f._elapsed ? `AI okuyor (${f._elapsed}s)` : 'AI okuyor...', cls: 'text-purple-700' };
        }
        if (f.status === 'saving') return { txt: 'Kaydediliyor...', cls: 'text-blue-700' };
        return null;
    };
    const ph = phaseLabel();

    const bgClass = isDup ? 'border-orange-300 bg-orange-50/60'
        : f.status === 'saved' ? 'border-emerald-300 bg-emerald-50/60'
        : f.status === 'error' ? 'border-red-200 bg-red-50/40'
        : f.status === 'done' && fileWarn ? 'border-amber-200 bg-amber-50/40'
        : 'border-[#E5E7EB] bg-white';

    return (
        <div className={`border rounded-xl p-3 ${bgClass}`} data-testid={`ocr-batch-row-${f.id}`}>
            <div className="flex items-start gap-3">
                {f.previewUrl ? (
                    <img src={f.previewUrl} alt="" className="w-14 h-14 object-cover rounded-md flex-shrink-0 border border-[#E5E7EB]" />
                ) : (
                    <div className="w-14 h-14 bg-purple-50 rounded-md flex items-center justify-center flex-shrink-0 text-purple-600 text-[9px] font-bold">PDF</div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-semibold text-[#111] truncate max-w-[400px]" title={f.file.name}>{f.file.name}</span>
                        {isDup ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-orange-500 text-white" data-testid={`ocr-batch-dup-badge-${f.id}`}>
                                ⊘ DUPLICATE
                            </span>
                        ) : (
                            <StatusBadge status={f.status} />
                        )}
                        <span className="text-[10px] text-[#9CA3AF]">{(f.file.size / 1024).toFixed(0)} KB</span>
                    </div>
                    {isDup && (
                        <p className="text-[11px] font-semibold text-orange-700 flex items-center gap-1" data-testid={`ocr-batch-dup-info-${f.id}`}>
                            ⊘ Bu fatura zaten kayitli - atlandi
                        </p>
                    )}
                    {ph && (
                        <p className={`text-[11px] font-semibold ${ph.cls} flex items-center gap-1`} data-testid={`ocr-batch-phase-${f.id}`}>
                            <RefreshCw size={10} className="animate-spin" />
                            {ph.txt}
                        </p>
                    )}
                    {ext && (f.status === 'done' || f.status === 'saving' || f.status === 'saved') && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-[11px] mt-1">
                            <div><span className="text-[#9CA3AF]">No:</span> <strong className={!ext.invoice_number ? 'text-red-500' : 'text-[#111]'}>{ext.invoice_number || '—'}</strong></div>
                            <div><span className="text-[#9CA3AF]">Tarih:</span> <strong>{ext.invoice_date || '—'}</strong></div>
                            <div><span className="text-[#9CA3AF]">Firma:</span> <strong className="truncate inline-block max-w-[140px] align-bottom">{ext.contact_name || '—'}</strong></div>
                            <div><span className="text-[#9CA3AF]">Tutar:</span> <strong className={!ext.total ? 'text-red-500' : 'text-emerald-600'}>{fmtMoney(ext.total)} {ext.currency}</strong></div>
                        </div>
                    )}
                    {f.error && (
                        <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1">
                            <AlertTriangle size={10} />{f.error}
                        </p>
                    )}
                    {fileWarn && f.status === 'done' && (
                        <p className="text-[11px] text-amber-600 mt-1">⚠ Fatura no veya tutar eksik - "Duzenle" ile tamamla</p>
                    )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                    {f.status === 'error' && (
                        <button onClick={onRetry} className="h-7 px-2 bg-purple-600 text-white text-[10px] font-bold rounded-md" data-testid={`ocr-batch-retry-${f.id}`}>
                            <RefreshCw size={10} className="inline mr-1" />Tekrar
                        </button>
                    )}
                    {f.status === 'done' && (
                        <button onClick={() => onEdit(f.id)} className="h-7 px-2 bg-white border border-[#E5E7EB] text-[#111] text-[10px] font-semibold rounded-md flex items-center gap-1" data-testid={`ocr-batch-edit-${f.id}`}>
                            <Edit2 size={10} />Duzenle
                        </button>
                    )}
                    {f.status !== 'saved' && f.status !== 'uploading' && f.status !== 'processing' && f.status !== 'saving' && (
                        <button onClick={() => onRemove(f.id)} className="h-6 px-2 bg-white border border-red-200 text-red-500 text-[10px] font-semibold rounded-md" data-testid={`ocr-batch-remove-${f.id}`}>
                            <X size={10} className="inline mr-0.5" />Sil
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending: ['bg-gray-100 text-gray-600', 'Bekliyor'],
        uploading: ['bg-amber-100 text-amber-700 animate-pulse', 'Yukleniyor'],
        processing: ['bg-blue-100 text-blue-700 animate-pulse', 'AI'],
        done: ['bg-emerald-50 text-emerald-700', 'Hazir'],
        saving: ['bg-blue-100 text-blue-700 animate-pulse', 'Kaydediyor'],
        saved: ['bg-emerald-600 text-white', 'Kaydedildi'],
        error: ['bg-red-50 text-red-600', 'Hata'],
    };
    const [cls, lbl] = map[status] || ['bg-gray-100', status];
    return <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${cls}`}>{lbl}</span>;
}

// ============ EDIT MODAL ============
function EditModal({ file, onClose, onSave }) {
    const ext = file.extracted || {};
    const [form, setForm] = useState({
        type: ext.type || 'purchase',
        invoice_number: ext.invoice_number || '',
        invoice_date: ext.invoice_date || new Date().toISOString().slice(0, 10),
        due_date: ext.due_date || '',
        contact_name: ext.contact_name || '',
        vat_number: ext.vat_number || '',
        iban: ext.iban || '',
        currency: ext.currency || 'EUR',
        subtotal: ext.subtotal || 0,
        vat_amount: ext.vat_amount || 0,
        total: ext.total || 0,
        payment_reference: ext.payment_reference || '',
    });
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <form
                onClick={e => e.stopPropagation()}
                onSubmit={(e) => {
                    e.preventDefault();
                    onSave({
                        ...ext, ...form,
                        subtotal: parseFloat(form.subtotal) || 0,
                        vat_amount: parseFloat(form.vat_amount) || 0,
                        total: parseFloat(form.total) || 0,
                    });
                }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3"
                data-testid="ocr-batch-edit-modal"
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-[#111]">OCR Sonucunu Duzenle</h3>
                    <button type="button" onClick={onClose} className="text-[#9CA3AF]"><X size={16} /></button>
                </div>
                <div className="flex gap-2">
                    {[['sales', 'Satis'], ['purchase', 'Alis']].map(([t, l]) => (
                        <button key={t} type="button" onClick={() => set('type', t)} className={`flex-1 h-8 rounded-lg text-[11px] font-semibold ${form.type === t ? 'bg-[#111827] text-white' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>{l}</button>
                    ))}
                </div>
                <input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="Fatura No *" required className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                <div className="grid grid-cols-2 gap-2">
                    <input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                    <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                </div>
                <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Firma Adi" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                <div className="grid grid-cols-2 gap-2">
                    <input value={form.vat_number} onChange={e => set('vat_number', e.target.value)} placeholder="KDV No" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                    <input value={form.iban} onChange={e => set('iban', e.target.value)} placeholder="IBAN" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <label className="text-[9px] text-[#9CA3AF]">Ara Toplam</label>
                        <input type="number" step="0.01" value={form.subtotal} onChange={e => { const v = e.target.value; set('subtotal', v); set('total', (parseFloat(v) || 0) + (parseFloat(form.vat_amount) || 0)); }} className="w-full h-8 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                    </div>
                    <div>
                        <label className="text-[9px] text-[#9CA3AF]">KDV</label>
                        <input type="number" step="0.01" value={form.vat_amount} onChange={e => { const v = e.target.value; set('vat_amount', v); set('total', (parseFloat(form.subtotal) || 0) + (parseFloat(v) || 0)); }} className="w-full h-8 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                    </div>
                    <div>
                        <label className="text-[9px] text-[#9CA3AF]">Toplam *</label>
                        <input type="number" step="0.01" value={form.total} onChange={e => set('total', e.target.value)} required className="w-full h-8 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs font-bold" />
                    </div>
                </div>
                <input value={form.payment_reference} onChange={e => set('payment_reference', e.target.value)} placeholder="Odeme Referansi" className="w-full h-9 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs" />
                <button type="submit" className="w-full h-9 bg-[#111827] text-white text-xs font-bold rounded-lg" data-testid="ocr-batch-edit-save">Guncelle ve Kaydet</button>
            </form>
        </div>
    );
}
